(() => {
  'use strict';

  window.LDPDPModules = window.LDPDPModules || {};

  const mountMotion = (root) => {
    const controller = new AbortController();
    const { signal } = controller;
    const scene = root.querySelector('[data-ld-engine-scene]');
    const chapters = Array.from(root.querySelectorAll('[data-ld-engine-chapter]'));
    const phaseMedia = Array.from(root.querySelectorAll('[data-ld-engine-phase-media]'));
    const mediaThumbs = Array.from(root.querySelectorAll('[data-ld-engine-media-thumb]'));
    const counter = root.querySelector('[data-ld-engine-phase-count]');
    const editorPhase = root.querySelector('[data-ld-engine-editor-phase]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileViewport = window.matchMedia('(max-width: 820px)');
    const routeMotionStyle = document.documentElement.dataset.ldProductMotion || 'chapters';
    root.dataset.motionStyle = routeMotionStyle;
    const motionEnabled = root.dataset.motion === 'true' && !reducedMotion.matches && routeMotionStyle !== 'calm';
    const mobileMotionEnabled = root.dataset.motionMobile === 'true';
    let activePhase = 0;
    let suspended = false;
    let scrollTrigger = null;
    let gsapContext = null;
    let raf = 0;

    const availableMediaPhases = phaseMedia
      .map((item) => Number(item.dataset.phase))
      .filter((phase) => Number.isFinite(phase))
      .sort((a, b) => a - b);

    const resolveMediaPhase = (requestedPhase) => {
      if (!availableMediaPhases.length) return null;
      const requested = Number(requestedPhase) || 0;
      if (availableMediaPhases.includes(requested)) return requested;

      const previous = availableMediaPhases.filter((phase) => phase <= requested).pop();
      return previous ?? availableMediaPhases[0];
    };

    const setMediaPhase = (requestedPhase) => {
      const resolvedPhase = resolveMediaPhase(requestedPhase);
      if (resolvedPhase == null) return;

      root.dataset.activeMediaPhase = String(resolvedPhase);

      phaseMedia.forEach((item) => {
        const isActive = Number(item.dataset.phase) === resolvedPhase;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', String(!isActive));
      });

      mediaThumbs.forEach((thumb) => {
        const isActive = Number(thumb.dataset.mediaPhase) === resolvedPhase;
        thumb.classList.toggle('is-active', isActive);
        if (isActive) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    };

    const setPhase = (phase, force = false) => {
      if (suspended && !force) return;
      const next = Math.max(0, Math.min(chapters.length - 1, Number(phase) || 0));
      activePhase = next;
      root.dataset.activePhase = String(next);
      root.style.setProperty('--ld-engine-progress', chapters.length > 1 ? String(next / (chapters.length - 1)) : '0');

      chapters.forEach((chapter) => {
        const chapterPhase = Number(chapter.dataset.phase || 0);
        const isActive = chapterPhase === next;
        chapter.classList.toggle('is-active', isActive);
        chapter.setAttribute('aria-hidden', mobileViewport.matches ? 'false' : String(!isActive));
      });

      if (counter) counter.textContent = `${String(next + 1).padStart(2, '0')} / ${String(chapters.length).padStart(2, '0')}`;
      if (editorPhase) editorPhase.textContent = `Fase ${next + 1} / ${chapters.length}`;
      setMediaPhase(next);
      root.dispatchEvent(new CustomEvent('ld:pdp:phase-change', { detail: { phase: next } }));
    };

    const updateFallback = () => {
      raf = 0;
      if (!scene || suspended) return;

      if (mobileViewport.matches) {
        const viewportTarget = window.innerHeight * 0.62;
        let closestPhase = activePhase;
        let closestDistance = Number.POSITIVE_INFINITY;
        chapters.forEach((chapter) => {
          const rect = chapter.getBoundingClientRect();
          const distance = Math.abs(rect.top + rect.height * 0.35 - viewportTarget);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestPhase = Number(chapter.dataset.phase || 0);
          }
        });
        setPhase(closestPhase);
        return;
      }

      const rect = scene.getBoundingClientRect();
      const scrollable = Math.max(rect.height - window.innerHeight, 1);
      const progress = Math.min(1, Math.max(0, -rect.top / scrollable));
      setPhase(Math.min(chapters.length - 1, Math.floor(progress * chapters.length)));
    };

    const requestFallback = () => {
      if (!raf) raf = window.requestAnimationFrame(updateFallback);
    };

    const initGsap = async () => {
      if (!motionEnabled || (mobileViewport.matches && !mobileMotionEnabled)) return;
      try {
        const loader = window.LDProductExperience?.loadMotionLibraries;
        if (typeof loader !== 'function') return;
        const { gsap, ScrollTrigger } = await loader(root);
        if (!gsap || !ScrollTrigger || !root.isConnected || mobileViewport.matches) return;

        gsapContext = gsap.context(() => {
          scrollTrigger = ScrollTrigger.create({
            trigger: scene,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 0.35,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              if (suspended) return;
              const phase = Math.min(chapters.length - 1, Math.floor(self.progress * chapters.length));
              setPhase(phase);
            }
          });
        }, root);
        root.classList.add('has-gsap');
      } catch (error) {
        root.dataset.motionFallback = 'true';
        console.warn('LD PDP: GSAP non disponibile, attivo fallback CSS', error);
      }
    };

    const observeForMotion = () => {
      if (!('IntersectionObserver' in window)) {
        initGsap();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        initGsap();
      }, { rootMargin: '100% 0px' });
      observer.observe(root);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    };

    root.addEventListener('ld:pdp:manual-media', (event) => {
      const requestedPhase = Number(event.detail?.phase);
      if (!Number.isFinite(requestedPhase)) return;
      setMediaPhase(requestedPhase);
    }, { signal });

    if (!motionEnabled || (mobileViewport.matches && !mobileMotionEnabled)) {
      root.classList.add('is-static');
      chapters.forEach((chapter) => {
        chapter.classList.add('is-active');
        chapter.setAttribute('aria-hidden', 'false');
      });
      setMediaPhase(0);

      return {
        setPhase(phase) {
          setMediaPhase(phase);
        },
        setMediaPhase,
        suspend() {},
        reset() {
          setMediaPhase(0);
          root.scrollIntoView({ behavior: 'auto', block: 'start' });
        },
        destroy() {
          controller.abort();
          root.classList.remove('is-static');
          chapters.forEach((chapter) => chapter.setAttribute('aria-hidden', 'false'));
          root.removeAttribute('data-active-media-phase');
        }
      };
    }

    window.addEventListener('scroll', requestFallback, { passive: true, signal });
    window.addEventListener('resize', () => {
      chapters.forEach((chapter) => chapter.setAttribute('aria-hidden', mobileViewport.matches ? 'false' : String(Number(chapter.dataset.phase || 0) !== activePhase)));
      requestFallback();
      window.ScrollTrigger?.refresh?.();
    }, { passive: true, signal });

    root.classList.add('is-enhanced');
    setPhase(0, true);
    requestFallback();
    observeForMotion();

    return {
      setPhase,
      setMediaPhase,
      suspend(value) {
        suspended = Boolean(value);
      },
      reset() {
        suspended = false;
        setPhase(0, true);
        root.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      },
      destroy() {
        controller.abort();
        scrollTrigger?.kill();
        gsapContext?.revert();
        if (raf) window.cancelAnimationFrame(raf);
        root.classList.remove('is-enhanced', 'is-static', 'has-gsap');
        root.removeAttribute('data-active-phase');
        root.removeAttribute('data-active-media-phase');
      }
    };
  };

  window.LDPDPModules.mountMotion = mountMotion;
})();

(() => {
  'use strict';

  const existing = window.LDPDPChapterSystem;
  if (existing?.version) {
    existing.refresh?.();
    return;
  }

  const globalController = new AbortController();
  const { signal } = globalController;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let chapters = [];
  let observer = null;
  let nav = null;
  let activeIndex = -1;
  let frame = 0;

  const chapterName = (chapter, index) => chapter.dataset.ldPdpChapterName || `Capitolo ${index + 1}`;

  const isRenderable = (node) => {
    if (!node?.isConnected || node.hidden) return false;
    const style = window.getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };

  const dispatchChapterEvent = (name, chapter, index, source) => {
    chapter.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: {
        chapter: chapterName(chapter, index),
        index,
        total: chapters.length,
        source
      }
    }));
  };

  const setActive = (nextIndex, source = 'observer') => {
    if (!chapters.length) return;
    const index = Math.max(0, Math.min(chapters.length - 1, Number(nextIndex) || 0));
    if (index === activeIndex && source !== 'refresh') return;
    activeIndex = index;

    chapters.forEach((chapter, chapterIndex) => {
      const active = chapterIndex === index;
      chapter.classList.toggle('is-ld-chapter-active', active);
      if (active) chapter.classList.add('has-ld-chapter-entered');
    });

    if (nav) {
      const count = nav.querySelector('[data-ld-pdp-chapter-count]');
      const label = nav.querySelector('[data-ld-pdp-chapter-current]');
      if (count) count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(chapters.length).padStart(2, '0')}`;
      if (label) label.textContent = chapterName(chapters[index], index);
      nav.style.setProperty('--ld-pdp-chapter-progress', String((index + 1) / chapters.length));
      nav.querySelectorAll('[data-ld-pdp-chapter-link]').forEach((link, linkIndex) => {
        if (linkIndex === index) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });
    }

    document.documentElement.dataset.ldPdpActiveChapter = String(index + 1);
    dispatchChapterEvent('ld:pdp-chapter:view', chapters[index], index, source);
  };

  const evaluateActiveChapter = () => {
    frame = 0;
    if (!chapters.length) return;
    const targetLine = window.innerHeight * 0.38;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    chapters.forEach((chapter, index) => {
      const rect = chapter.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      const chapterLine = Math.max(rect.top, Math.min(rect.bottom, targetLine));
      const distance = Math.abs(chapterLine - targetLine);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    if (!Number.isFinite(bestDistance)) {
      const preceding = chapters
        .map((chapter, index) => ({ index, top: chapter.getBoundingClientRect().top }))
        .filter((item) => item.top <= targetLine)
        .pop();
      bestIndex = preceding?.index ?? 0;
    }

    setActive(bestIndex);
  };

  const scheduleEvaluation = () => {
    if (!frame) frame = window.requestAnimationFrame(evaluateActiveChapter);
  };

  const buildNav = () => {
    const element = document.createElement('nav');
    element.className = 'ld-pdp-chapter-nav';
    element.setAttribute('aria-label', 'Capitoli del prodotto');
    element.innerHTML = `
      <div class="ld-pdp-chapter-nav__current" aria-live="polite" aria-atomic="true">
        <span class="ld-pdp-chapter-nav__count" data-ld-pdp-chapter-count></span>
        <span class="ld-pdp-chapter-nav__label" data-ld-pdp-chapter-current></span>
      </div>
      <span class="ld-pdp-chapter-nav__meter" aria-hidden="true"><span></span></span>
      <ol class="ld-pdp-chapter-nav__list"></ol>
    `;

    const list = element.querySelector('.ld-pdp-chapter-nav__list');
    chapters.forEach((chapter, index) => {
      if (!chapter.id) chapter.id = `LDProductChapter-${index + 1}`;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${chapter.id}`;
      link.dataset.ldPdpChapterLink = '';
      link.innerHTML = `<span>${String(index + 1).padStart(2, '0')}</span><span>${chapterName(chapter, index)}</span>`;
      item.appendChild(link);
      list.appendChild(item);
    });

    element.addEventListener('click', (event) => {
      const link = event.target.closest('[data-ld-pdp-chapter-link]');
      if (!link) return;
      const index = Array.from(element.querySelectorAll('[data-ld-pdp-chapter-link]')).indexOf(link);
      const target = chapters[index];
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      try {
        window.history.replaceState(window.history.state, '', `#${target.id}`);
      } catch (_) {
        // The real anchor remains valid when History API is unavailable.
      }
      setActive(index, 'navigate');
      dispatchChapterEvent('ld:pdp-chapter:navigate', target, index, 'progress');
    });

    return element;
  };

  const refresh = () => {
    observer?.disconnect();
    observer = null;
    nav?.remove();
    nav = null;
    activeIndex = -1;

    chapters = Array.from(document.querySelectorAll('[data-ld-pdp-chapter]'))
      .filter(isRenderable)
      .sort((a, b) => {
        const aIndex = Number(a.dataset.ldPdpChapterIndex || 999);
        const bIndex = Number(b.dataset.ldPdpChapterIndex || 999);
        return aIndex - bIndex;
      });

    document.documentElement.classList.toggle('ld-pdp-chapters-enhanced', chapters.length > 1);
    if (chapters.length < 2) return;

    nav = buildNav();
    document.body.appendChild(nav);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('has-ld-chapter-entered');
        });
        scheduleEvaluation();
      }, {
        rootMargin: '-18% 0px -48% 0px',
        threshold: [0, 0.05, 0.25, 0.5]
      });
      chapters.forEach((chapter) => observer.observe(chapter));
    }

    setActive(0, 'refresh');
    scheduleEvaluation();
  };

  const destroy = () => {
    globalController.abort();
    observer?.disconnect();
    if (frame) window.cancelAnimationFrame(frame);
    nav?.remove();
    chapters.forEach((chapter) => chapter.classList.remove('is-ld-chapter-active', 'has-ld-chapter-entered'));
    chapters = [];
    document.documentElement.classList.remove('ld-pdp-chapters-enhanced');
    document.documentElement.removeAttribute('data-ld-pdp-active-chapter');
  };

  window.addEventListener('scroll', scheduleEvaluation, { passive: true, signal });
  window.addEventListener('resize', scheduleEvaluation, { passive: true, signal });
  window.addEventListener('pageshow', scheduleEvaluation, { signal });
  document.addEventListener('shopify:section:load', () => window.queueMicrotask(refresh), { signal });
  document.addEventListener('shopify:section:unload', () => window.queueMicrotask(refresh), { signal });
  document.addEventListener('shopify:section:select', (event) => {
    const chapter = event.target.querySelector?.('[data-ld-pdp-chapter]') || event.target.closest?.('[data-ld-pdp-chapter]');
    const index = chapters.indexOf(chapter);
    if (index >= 0) setActive(index, 'editor');
  }, { signal });

  window.LDPDPChapterSystem = { version: '1.0.0', refresh, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true, signal });
  } else {
    refresh();
  }
})();
